import { useCallback, useRef, type ComponentProps, type CSSProperties } from 'react';
import { Page } from 'react-pdf';

type PageProps = ComponentProps<typeof Page>;
type PageRenderCallback = NonNullable<PageProps['onRenderSuccess']>;

type PdfSeamlessPageProps = PageProps & {
  /** Classes du calque qui superpose l'instantané et la page ; doit cadrer comme le canvas rendu. */
  wrapperClassName?: string;
  /**
   * Dimensions du calque. Impératif s'il n'est pas dimensionné par ses classes : sans taille propre,
   * il s'écrase quand react-pdf retire son canvas et l'instantané disparaît avec lui.
   */
  wrapperStyle?: CSSProperties;
};

/**
 * Page PDF sans flash au changement de page : react-pdf vide son canvas dès que `pageNumber`
 * change et ne le repeint qu'au terme d'un rendu asynchrone. On conserve donc une copie de la
 * dernière page rendue dans un canvas de fond, que la page suivante vient recouvrir une fois peinte.
 * Ne jamais remonter ce composant sur changement de page (pas de `key` indexée dessus).
 */
export default function PdfSeamlessPage({
  wrapperClassName = 'relative',
  wrapperStyle,
  onRenderSuccess,
  ...pageProps
}: PdfSeamlessPageProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const snapshotRef = useRef<HTMLCanvasElement>(null);

  const handleRenderSuccess = useCallback<PageRenderCallback>(
    (page) => {
      const snapshot = snapshotRef.current;
      const rendered = wrapperRef.current?.querySelector<HTMLCanvasElement>(
        'canvas:not([data-pdf-snapshot])',
      );

      if (snapshot && rendered && rendered.width > 0 && rendered.height > 0) {
        snapshot.width = rendered.width;
        snapshot.height = rendered.height;
        snapshot.getContext('2d')?.drawImage(rendered, 0, 0);
      }

      onRenderSuccess?.(page);
    },
    [onRenderSuccess],
  );

  return (
    <div ref={wrapperRef} className={wrapperClassName} style={wrapperStyle}>
      <canvas
        ref={snapshotRef}
        data-pdf-snapshot=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-contain pointer-events-none"
      />
      <Page {...pageProps} onRenderSuccess={handleRenderSuccess} loading={null} />
    </div>
  );
}
